/**
 * Code-only warm-up for the authenticated route graph.
 *
 * Why this exists: the five tab roots feel instant because something warms them
 * ahead of the tap (`AuthenticatedWarmStart` for likely-next tabs,
 * `BottomNavigation` on touch). Every other page only warms when one of its own
 * cards happens to be on screen with `preload="viewport"`. Any other way in — a
 * back-adjacent jump, an onClick handler that cannot be a `<Link>`, a PWA
 * relaunch straight onto a subpage — paid the full chunk download plus JS parse
 * at tap time. On a phone that parse is what the user feels, not the bytes
 * (desktop on the same connection is fine).
 *
 * So: warm the whole authenticated subtree's lazy modules once, after first
 * paint, one module at a time through the shared idle queue. Modules only — no
 * loaders, no queries — so warming never costs an API request and a navigation
 * still streams its data into the destination's own skeleton as before.
 */
import { enqueueIdleTask } from '@/lib/prefetch/idle';

type RouteModuleLoaders = Record<string, () => Promise<unknown>>;

/**
 * Every code-split component the signed-in app can reach: the authenticated
 * subtree plus the public entity pages (class / studio / instructor detail),
 * which are reached from discovery cards, share links and back-adjacent jumps.
 * Vite keeps these split (dynamic imports), so this adds only a small map of
 * import functions to the entry chunk.
 */
const authenticatedRouteModules = import.meta.glob<RouteModuleLoaders[string]>([
  '../../routes/_authenticated/**/*.lazy.tsx',
  '../../routes/_public/**/*.lazy.tsx',
]);

export { authenticatedRouteModules };

/**
 * Likelihood a learner reaches the group next, most likely first. Ties keep
 * Vite's glob order so the queue is deterministic.
 */
const GROUP_PRIORITY = [
  '_authenticated/profile/',
  '_authenticated/feature-requests/',
  '_authenticated/community/',
  '_authenticated/market/',
  '_authenticated/home/',
  '_authenticated/discovery',
  '_public/',
  '_authenticated/listings/',
  '_authenticated/data-reports/',
];

function priorityOf(path: string): number {
  const index = GROUP_PRIORITY.findIndex((group) => path.includes(group));
  return index === -1 ? GROUP_PRIORITY.length : index;
}

/** Warm order: the reported-slow subtrees first, forms and one-offs last. */
export function getRouteGraphWarmOrder(loaders: RouteModuleLoaders): string[] {
  return Object.keys(loaders).sort((a, b) => priorityOf(a) - priorityOf(b));
}

/**
 * The warm tasks, in order. Pure, so the queue wiring below stays a one-liner
 * and the ordering/error policy is testable without the idle queue.
 */
export function routeGraphWarmTasks(
  loaders: RouteModuleLoaders = authenticatedRouteModules
): Array<() => Promise<unknown>> {
  return getRouteGraphWarmOrder(loaders).map(
    (path) => () =>
      // Best-effort: a failed chunk warm must never stop the rest of the graph.
      (loaders[path]?.() ?? Promise.resolve()).catch(() => undefined)
  );
}

/**
 * Enqueue the whole authenticated route graph on the shared idle queue, which
 * runs one task at a time so warming can never compete with the page on screen.
 * Callers gate this on connection quality; the page still loads its own chunk on
 * demand, so a skipped or failed warm only costs the old behaviour.
 */
export function warmAuthenticatedRouteGraph(
  loaders: RouteModuleLoaders = authenticatedRouteModules
): void {
  for (const task of routeGraphWarmTasks(loaders)) enqueueIdleTask(task);
}
